import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  ApiKeyCreated,
  ApiKeyItem,
  ApiKeyListResponse,
  CreateApiKeyPayload,
  UpdateApiKeyPayload,
} from "../types";

const KEY = ["api-keys"] as const;
const BASE = "/admin/api-keys";

export function useApiKeyList(
  params: { source?: string; status?: string } = {},
): UseQueryResult<ApiKeyListResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<ApiKeyListResponse>(BASE, {
        token: accessToken,
        query: { source: params.source, status: params.status, limit: 100 },
      }),
  });
}

export function useApiKey(id: string): UseQueryResult<ApiKeyItem> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ apiKey: ApiKeyItem }>(`${BASE}/${id}`, {
        token: accessToken,
      });
      return res.apiKey;
    },
  });
}

export function useCreateApiKey() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateApiKeyPayload) => {
      const res = await apiFetch<{ apiKey: ApiKeyCreated }>(BASE, {
        method: "POST",
        body: payload,
        token: accessToken,
      });
      return res.apiKey;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateApiKey(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateApiKeyPayload) =>
      apiFetch<{ apiKey: ApiKeyItem }>(`${BASE}/${id}`, {
        method: "PATCH",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRevokeApiKey() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ apiKey: ApiKeyItem }>(`${BASE}/${id}/revoke`, {
        method: "POST",
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteApiKey() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`${BASE}/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
